import React, { useState } from "react"
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Typography, useTheme } from "@mui/material"
import PersonIcon from "@mui/icons-material/PersonOutlined"
import { useSnackbar } from "src/providers"
import { inviteTeamMember } from "src/services/api/companyMembers"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import { Community } from "src/models/user"

interface InviteMemberDialogProps {
  open: boolean
  onClose: () => void
  onInvited: () => void
  teamId: string
  communityOptions: Community[]
}

const isValidEmail = (e: string) => e.includes("@")

export const InviteMemberDialog = ({ open, onClose, onInvited, teamId, communityOptions }: InviteMemberDialogProps) => {
  const { snackbarShowMessage } = useSnackbar()
  const { palette } = useTheme()
  const [emails, setEmails] = useState<string[]>([])
  const [inputValue, setInputValue] = useState("")
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedCommunities, setSelectedCommunities] = useState<Community[]>([])

  const effectiveEmails = inputValue.trim() ? [...emails, inputValue.trim()] : emails
  const canInvite = effectiveEmails.length > 0 && effectiveEmails.every(isValidEmail)
  const showError = touched && !canInvite

  const commitInput = () => {
    const val = inputValue.trim()
    if (!val) return
    setEmails((prev) => [...prev, val])
    setInputValue("")
  }

  const handleClose = () => {
    setEmails([])
    setInputValue("")
    setTouched(false)
    setSelectedCommunities([])
    onClose()
  }

  const handleInvite = () => {
    setTouched(true)
    commitInput()
    if (!canInvite) return
    const toInvite = inputValue.trim() ? [...emails, inputValue.trim()] : emails
    setSubmitting(true)
    inviteTeamMember(
      teamId,
      toInvite,
      ["ROLE_BUYER", "ROLE_KNOWLEDGE_MINER", "ROLE_DATA_SOURCE_ADMIN"],
      selectedCommunities.map((c) => c.gatedCommunityId)
    )
      .then(() => {
        snackbarShowMessage("Member invited", "success")
        handleClose()
        onInvited()
      })
      .catch(() => snackbarShowMessage("Failed to invite member", "error"))
      .finally(() => setSubmitting(false))
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "25px", pt: 4, px: 4 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, fontSize: "32px", fontWeight: 600, px: 0 }}>
        <PersonIcon sx={{ fontSize: 36 }} />
        Invite Team Member/s
      </DialogTitle>
      <DialogContent sx={{ px: 0, py: 2 }}>
        <Stack spacing={3}>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>E-mail/s</Typography>
            <CustomAutocomplete<string>
              multiple
              freeSolo
              options={[]}
              value={emails}
              inputValue={inputValue}
              getOptionLabel={(opt) => opt}
              onChange={(_, newValue) => setEmails(newValue as string[])}
              onInputChange={(_, newValue) => {
                if (newValue.endsWith(",") || newValue.endsWith(";") || newValue.endsWith(" ")) {
                  const val = newValue.slice(0, -1).trim()
                  if (val) setEmails((prev) => [...prev, val])
                  setInputValue("")
                } else {
                  setInputValue(newValue)
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    color={isValidEmail(option) ? "default" : "error"}
                    {...getTagProps({ index })}
                  />
                ))
              }
              size="small"
              helperText={showError ? "Valid email(s) required" : undefined}
            /></Stack>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Add member/s to the Knowledge Base/s:</Typography>
            <CustomAutocomplete<Community>
              value={selectedCommunities}
              options={communityOptions.filter((c) => c.gatedCommunityName !== "My Knowledge Base")}
              getOptionLabel={(opt) => opt.gatedCommunityName}
              isOptionEqualToValue={(a, b) => a.gatedCommunityId === b.gatedCommunityId}
              onChange={(_, val) => setSelectedCommunities((Array.isArray(val) ? val : []) as Community[])}
              size="small"
              multiple
            />
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Roles</Typography>
            <Typography variant="body2" color="text.primary">
              New members are granted Requester, Knowledge Miner and Data Source Admin roles.{" "}
              In case other roles are needed contact us via{" "}
              <Link href="/help" sx={{ textDecoration: "none", color: palette.text.primary }}>Exfluency Help</Link>
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 0, pb: 2, pt: 3, gap: 0.5 }}>
        <Button variant="contained" color="primary" onClick={handleInvite} disabled={submitting}>
          Invite
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleClose}>
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  )
}
